import React, { useEffect, useState } from "react";
import {
  Button,
  Upload,
  Input,
  Spin,
  Divider,
  Form,
  Typography,
  Progress,
  Card,
  Tooltip,
  message,
  Select,
  notification,
} from "antd";
import {
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { RcFile } from "antd/es/upload";
import { getAllMeetings, getMeetingTranscript, onboarding } from "api/onboarding.api";
import { getFireFliesAPIKey } from "api/intergations.api";

interface FirstStepOnboardingFormProps {
  onNext: (response: any) => void;
}

const FirstStepOnboardingForm: React.FC<FirstStepOnboardingFormProps> = ({ onNext }) => {
  const [form] = Form.useForm();
  const [uploadedFile, setUploadedFile] = useState<any | null>(null);
  const [isTextAreaDisabled, setIsTextAreaDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [meetingsData, setMeetingsData] = useState<any[]>([]);
  const [isFireFliesKeySet, setIsFireFliesKeySet] = useState<boolean>(false);
  const [fireFliesKey, setFireFliesKey] = useState<string>("");
  const [meetingTranscriptData, setMeetingTranscriptData] = useState<string>("");
  const [meetingOptions, setMeetingOptions] = useState<any[]>([]);
  const [meetingTranscriptResponse, setMeetingTranscriptResponse] = useState<any[]>([]);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [isFirefliesEnabled, setIsFirefliesEnabled] = useState(false);

  const handleFileChange = (info: any) => {
    const file: RcFile = info;
    const fileType = file.type;

    // Validate file type
    if (
      ![
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(fileType)
    ) {
      form.setFields([
        {
          name: "files",
          errors: ["Only PDF and DOCX files are allowed."],
        },
      ]);
      return;
    }

    // Set file details
    const fileSize = `${(file.size / 1024).toFixed(2)} KB`;
    const fileUrl = URL.createObjectURL(file);

    setUploadedFile({
      name: file.name,
      size: fileSize,
      url: fileUrl,
      progress: 100, // Simulated upload progress
      file: file, // Store the actual file object
    });

    setIsTextAreaDisabled(true); // Disable text area
    form.setFieldsValue({ files: file }); // Update the field value
    form.setFieldsValue({ description: "" }); // Clear text area

    // Clear validation error for the "files" field
    form.validateFields(["files", "description"]); // Re-validate description and files fields
  };

  const handleDelete = () => {
    setUploadedFile(null);
    setIsTextAreaDisabled(false); // Enable text area
    form.setFieldsValue({ files: null }); // Reset file input

    // Re-validate both fields
    form.validateFields(["files", "description"]);
  };

  const fetchOnbaordingDescription = async (file: File | null, description: string | null) => {
    try {
      const formData = new FormData();

      if (file) {
        formData.append("file", file); // Send the file directly
      } else if (description) {
        formData.append("text", description); // Send description as fallback
      }
      const response = await onboarding(formData);
      console.log("API Response:", response);
      return response;
    } catch (error) {
      message.error("There was an error extracting text from the file.");
      return;
    }
  };

  const handleAnalyze = async () => {
    try {
      // Validate the form
      setLoading(true);
      const values = await form.validateFields();
      console.log("Form is valid. Proceeding to next step...");

      let fileToSend: File | null = null;
      let descriptionToSend: string | null = null;

      if (values.files && uploadedFile?.file) {
        // Extract text from the uploaded file
        fileToSend = uploadedFile.file.originFileObj || uploadedFile.file; // Get the actual File object
        // fileToSend = await readFileContent(file); // Helper function to read file
      } else if (values.description) {
        // Use the description field
        descriptionToSend = values.description;
      }
      const onboardingDescription = await fetchOnbaordingDescription(fileToSend, descriptionToSend);
      message.success("Analysis Completed Successfully");
      onNext(onboardingDescription);
    } catch (error) {
      console.log("Validation Failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const description = e.target.value;
    // Clear file if description is entered
    setUploadedFile(null);
    setIsTextAreaDisabled(false); // Enable file upload
    // Update the "files" field validation dynamically
    form.setFieldsValue({ files: null, description }); // Update field values
    setMeetingTranscriptData(description);
    form.validateFields(["files", "description"]); // Re-validate fields
  };

  const fetchMeetingTranscript = async (meetingId: string) => {
    try {
      setMeetingLoading(true);
      const response = await getMeetingTranscript(meetingId);
      // if (Array.isArray(response)) {
      //     setMeetingTranscriptResponse(response);
      // }
      setMeetingTranscriptData(response);
      form.setFieldsValue({ files: null, description: response });
      console.log("Meetings Data:", response);
    } catch (error) {
      console.error("Error fetching get all meeting list:", error);
      notification.error({
        message: "Failed!",
        description: String(error) || "An unknown error occurred.",
      });
    } finally {
      setMeetingLoading(false);
    }
  };

  const handleSelectChange = async (value: string) => {
    console.log("Selected value:", value);
    await fetchMeetingTranscript(value);
  };

  useEffect(() => {
    console.log("Meeting Transcript Data:", meetingTranscriptData);
    const conversationString: string = meetingTranscriptResponse
      .map((entry) => `${entry.speaker}: ${entry.text}`)
      .join("\n");
    setMeetingTranscriptData(conversationString);
    form.setFieldsValue({ files: null, description: conversationString });
  }, [meetingTranscriptResponse]);

  const fetchMeetingList = async () => {
    try {
      const response: any = await getAllMeetings();
      console.log("Meetings Data:", response);
      setMeetingsData(response);
      setMeetingOptions(
        response.map((item: any) => ({
          value: item.id,
          label: `${item.title} (${new Date(item.date).toLocaleDateString()})`,
        }))
      );
    } catch (error) {
      console.error("Error fetching get all meeting list:", error);
      // notification.error({
      //     message: "Failed!",
      //     description: String(error) || "An unknown error occurred.",
      // });
    }
  };

  const fetchFireFliesAPIKey = async () => {
    try {
      setLoading(true);
      const response: any = await getFireFliesAPIKey();
      setIsFirefliesEnabled(response.key !== null);
    } catch (error) {
      console.error("Error fetching get all meeting list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetingList();
    fetchFireFliesAPIKey();
    console.log("Meeting Data:", meetingsData);
  }, []);

  return (
    <>
      <Form form={form} layout="vertical">
        {/* File Upload */}
        <Form.Item
          name="files"
          tooltip="Upload file for reference"
          dependencies={["description"]}
          rules={[
            {
              validator: async (_, value) => {
                if (!value && !form.getFieldValue("description")) {
                  throw new Error("Please upload a file or enter a description.");
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Upload.Dragger
            name="files"
            multiple={false}
            maxCount={1}
            accept=".docx,.pdf"
            beforeUpload={(file) => {
              // You can process the file here
              handleFileChange(file);
              return false; // Prevent upload
            }}
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon">
              <CloudUploadOutlined />
            </p>
            <p className="ant-upload-text">
              <span
                style={{
                  color: "#8a2be2",
                  cursor: "pointer",
                }}
              >
                Click to upload
              </span>{" "}
              or drag and drop
            </p>
            <p className="ant-upload-hint">PDF and Docx files supported</p>
          </Upload.Dragger>
          <div
            style={{
              marginTop: "10px",
              border: "1px solid #d9d9d9",
              borderRadius: "8px",
              padding: "10px",
              backgroundColor: "#f5f5f5",
              transition: "border-color 0.3s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#8a2be2")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#d9d9d9")}
          >
            {uploadedFile ? (
              <div
                style={{
                  pointerEvents: loading ? "none" : "auto", // Disable interaction when loading
                  opacity: loading ? 0.5 : 1, // Dim the content when loading
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      width: "90%",
                    }}
                  >
                    {/* <FilePdfOutlined style={{ fontSize: '24px', color: '#e74c3c' }} /> */}
                    <a
                      href={uploadedFile.url}
                      style={{ textDecoration: "none", color: "#1890ff" }}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Typography.Text strong>
                        {uploadedFile.name}
                        <p style={{ fontWeight: "1" }}>{uploadedFile.size}</p>
                      </Typography.Text>
                    </a>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "35px",
                        color: "darkgray",
                        cursor: "pointer",
                        padding: "5px",
                        borderRadius: "5px",
                        transition: "background-color 0.5s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <DownloadOutlined />
                    </div>
                    <Button
                      icon={<DeleteOutlined />}
                      style={{ color: "darkgray" }}
                      type="text"
                      onClick={handleDelete}
                    />
                  </div>
                </div>
                {/* Progress Bar */}
                <div style={{ marginTop: "-15px" }}>
                  <Progress percent={uploadedFile.progress} size="small" strokeColor="#8a2be2" />
                </div>
              </div>
            ) : (
              <Typography.Text>No file uploaded yet</Typography.Text>
            )}
          </div>
        </Form.Item>

        {/* OR Divider */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            margin: "10px 0",
            marginBottom: "-5px",
          }}
        >
          <div style={{ flex: 1, height: "1px", backgroundColor: "#d9d9d9" }}></div>
          <span style={{ padding: "0 10px", fontSize: "14px", color: "#999" }}>OR</span>
          <div style={{ flex: 1, height: "1px", backgroundColor: "#d9d9d9" }}></div>
        </div>

        {isFirefliesEnabled ? (
          <>
            {/* Select Field and Button */}
            <Spin spinning={loading}>
              <div
                className="input-row-wrapper"
                style={{
                  marginBottom: "0",
                  marginTop: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "1px",
                }}
              >
                {/* Label */}
                <label htmlFor="select-field" style={{ minWidth: "145px", fontWeight: "bold" }}>
                  Extract from Fireflies:
                </label>

                {/* Select Field */}
                <Select
                  id="select-field"
                  placeholder="Select a transcript"
                  onChange={(value) => handleSelectChange(value)} // Updated handler
                  style={{ flex: 1 }} // Makes the Select take available space
                  options={meetingOptions}
                />
              </div>
            </Spin>
          </>
        ) : (
          <>
            <Spin spinning={loading}>
              <div style={{ marginTop: "12px", color: "red", textAlign: "center" }}>
                Fireflies feature is not enabled. Please enable it to proceed.
              </div>
            </Spin>
            {/* <div className="input-button-wrapper" style={{ marginBottom: "16px", display: "flex", gap: "8px" }}>
                            <Input
                                placeholder="Enter FireFlies Key"
                                onChange={(e) => setFireFliesKey(e.target.value)}
                                style={{ flex: 1 }} // Makes input take available space
                            />
                            <Button type="primary" onClick={handleSetFireFliesKey}>
                                Set Fireflies Key
                            </Button>
                        </div> */}
          </>
        )}

        {/* Text Area */}
        <Spin spinning={meetingLoading} size="large">
          <Form.Item
            name="description"
            dependencies={["files"]}
            rules={[
              {
                validator: async (_, value) => {
                  if (!value && !uploadedFile) {
                    throw new Error("Please enter a description or upload a file.");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <div className="custom-card">
              <Card title="Please review and edit details *" bordered={false}>
                <Input.TextArea
                  rows={10}
                  style={{ width: "100%" }}
                  disabled={isTextAreaDisabled} // Disable textarea based on file upload
                  placeholder="Enter description here..."
                  value={meetingTranscriptData}
                  onChange={handleDescriptionChange}
                />
                {/* <Tooltip title="This is a hint text to help user." placement="bottom">
                                    <p style={{ color: "gray", marginTop: "10px" }}>This is a hint text to help user.</p>
                                </Tooltip> */}
              </Card>
            </div>
          </Form.Item>
        </Spin>

        {/* Footer Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
          <Button
            // type="primary"
            onClick={handleAnalyze}
            className="text-white py-1 px-3 rounded-md bg-[#845dda] border border-[#7F56D9]"
          >
            {loading ? (
              <Spin spinning={loading} size="small" className="custom-spin" />
            ) : (
              <SaveOutlined />
            )}
            Analyze
          </Button>
        </div>
      </Form>
    </>
  );
};

export default FirstStepOnboardingForm;
